import { z } from 'zod'
import { v4 as uuidv4 } from 'uuid'
import { componentIdSchema } from '@customTypes/cloudmix/sessions/components/base'
import { commandSchema } from '@features/projects/types/actions'

export const macroIdSchema = z.string().default(() => uuidv4())

export const MACRO_ACTION_TYPES = {
  delay: 'delay',
  command: 'command',
  sequence: 'sequence',
} as const

export const MACRO_EXECUTION_STATUSES = {
  Idle: 'idle',
  Running: 'running',
} as const

export const macroExecutionStatusSchema = z.nativeEnum(MACRO_EXECUTION_STATUSES)

export const MACRO_ACTION_STATUSES = {
  Pending: 'pending',
  Running: 'running',
  Complete: 'complete',
} as const

export const macroActionStatusSchema = z.nativeEnum(MACRO_ACTION_STATUSES)

// TODO(ajb): Update all remote commands to be match engine
export const remoteCommandSchema = z.object({
  name: z.union([commandSchema, z.string()]), // String to prevent issues with unknown commands
  args: z.unknown().optional(),
})

export type RemoteCommand = z.infer<typeof remoteCommandSchema>

const baseMacroActionSchema = z.object({
  type: z.string(),
  startTimeMs: z.number().nullable().optional(),
  status: macroActionStatusSchema.optional(),
})

export const delayMacroActionSchema = baseMacroActionSchema.extend({
  type: z.literal(MACRO_ACTION_TYPES.delay),
  delayMs: z.number().int().nonnegative(),
})

export const commandMacroActionSchema = baseMacroActionSchema.extend({
  type: z.literal(MACRO_ACTION_TYPES.command),
  command: remoteCommandSchema,
  componentId: componentIdSchema.nullish(),
})

// Lazy for recursive schema - Sequences can contain other MacroActions
export const macroActionSchema: z.ZodType<MacroAction> = z.lazy(() =>
  z.discriminatedUnion('type', [
    delayMacroActionSchema,
    commandMacroActionSchema,
    sequenceMacroActionSchema,
  ])
)

export const sequenceMacroActionSchema = baseMacroActionSchema.extend({
  type: z.literal(MACRO_ACTION_TYPES.sequence),
  actions: z.array(macroActionSchema),
  currentStep: z.number().nullable().optional(),
})

export const macroSchema = z.object({
  displayName: z.string().nullish().optional(),
  macroId: macroIdSchema,
  action: macroActionSchema,
  status: macroExecutionStatusSchema.optional(),
})

export type MacroId = z.infer<typeof macroIdSchema>
export type DelayMacroAction = z.infer<typeof delayMacroActionSchema>
export type CommandMacroAction = z.infer<typeof commandMacroActionSchema>
export type MacroActionStatus =
  (typeof MACRO_ACTION_STATUSES)[keyof typeof MACRO_ACTION_STATUSES]
export type SequenceMacroAction = {
  type: typeof MACRO_ACTION_TYPES.sequence
  actions: MacroAction[]
  currentStep?: number | null
  startTimeMs?: number | null
  status?: MacroActionStatus
}
export type MacroAction =
  | DelayMacroAction
  | CommandMacroAction
  | SequenceMacroAction
export type Macro = z.infer<typeof macroSchema>
export type MacroExecutionStatus =
  (typeof MACRO_EXECUTION_STATUSES)[keyof typeof MACRO_EXECUTION_STATUSES]
